/**
 * Admin routes. Owner: admin portal. All render under PortalLayout (no site chrome).
 * A function (not a constant) so flag() is read on every render.
 *
 * SLOT_MODEL (Phase 9) swaps the admin portal's everyday pages to the rebuilt slot pages on
 * the same URLs, all lazy and still behind RequireAdmin: /admin/locations (AdminVenues),
 * /admin/locations/:locationId (AdminVenueDetail), /admin/businesses (AdminBusinesses),
 * /admin/businesses/:businessId/edit (AdminBusinessDetail), /admin/customers (AdminCustomers),
 * /admin/customers/:customerId (AdminCustomerDetail), /admin/venue-applications
 * (AdminApplications), /admin/email-templates (AdminEmailTemplates) and /admin/subscriptions
 * (AdminProducts, "Products & prices"). New, flag on only: /admin/inventory,
 * /admin/category-map, /admin/waitlist, /admin/categories, /admin/renewals, /admin/billing,
 * /admin/print and /admin/print/:cycleId; /admin/subscription-tiers redirects (replace) to
 * /admin/subscriptions, and /admin/coaster-designs to /admin/print (Coaster Designs is retired
 * with the flag on). The classic editors stay reachable as escape hatches, flag on only:
 * /admin/locations/:locationId/legacy (LocationDetail: QR, live coaster, contacts) and
 * /admin/businesses/:businessId/legacy (AdminBusinessEdit: offers).
 * Phase 11 (SLOT_MODEL): /admin/dashboard swaps to AdminOverview (any admin), and
 * /admin/owner (OwnerKpi, the owner's plan-vs-actual dashboard; the API answers 403 not_owner
 * to any other admin and the page says so) is new, flag on only.
 * Site redesign Phase 8 (SLOT_MODEL): the last legacy admin pages still reachable with the flag
 * on swap to rebuilt lazy pages on the same URLs: /admin/customers/archived
 * (AdminArchivedCustomers), /admin/businesses/archived (AdminArchivedBusinesses),
 * /admin/locations/archived (AdminArchivedVenues), /admin/email-templates/:templateId/preview
 * (AdminEmailPreview) and /admin/profile (AdminProfilePage).
 * Public-site redesign (after the enquiries migration, SLOT_MODEL): /admin/enquiries
 * (AdminEnquiries, the enquiries the site's forms store) is new, flag on only.
 * Coaster Studio (SLOT_MODEL + COASTER_STUDIO; the Studio lives in the rebuilt admin portal):
 * /admin/coaster-studio (StudioProjects, the designs list), /admin/coaster-studio/new (StudioNew,
 * ?location_id= presets the venue), /admin/coaster-studio/settings (StudioSettings, owner model
 * and spend controls) and /admin/coaster-studio/:projectId (StudioWorkspace, the working screen).
 * New, lazy, both flags on only; the static paths are declared before the param.
 *
 * The slot pages frame themselves with PortalShell (pages/admin/slot/adminFrame.jsx); the legacy
 * pages wrap themselves in AdminLayout + AdminNavigation, so nothing here adds a layout. The
 * static segments ("archived", "legacy", "edit") outrank :params in React Router's ranking.
 * With the flag off nothing changes: the new paths are not declared (they fall to the admin
 * catch-all, NotFound without site chrome) and every page is the legacy one.
 */
import React from "react";
import { Route, Navigate } from "react-router-dom";
import { flag } from "../config";
import { RequireAdmin } from "../components/routing/guards";
import NotFound from "../pages/NotFound";
import AdminLogin from "../pages/admin/AdminLogin";
import AdminDashboard from "../pages/admin/AdminDashboard";
import AdminProfile from "../pages/admin/AdminProfile";
import AdminCustomers from "../pages/admin/AdminCustomers";
import ArchivedCustomers from "../pages/admin/ArchivedCustomers";
import CustomerDetails from "../pages/admin/CustomerDetails";
import BusinessManagement from "../pages/admin/BusinessManagement";
import AdminBusinessEdit from "../pages/admin/AdminBusinessEdit";
import ArchivedBusinesses from "../pages/admin/ArchivedBusinesses";
import LocationManagement from "../pages/admin/LocationManagement";
import LocationDetail from "../pages/admin/LocationDetail";
import ArchivedLocations from "../pages/admin/ArchivedLocations";
import VenueApplications from "../pages/admin/VenueApplications";
import CoasterDesigns from "../pages/admin/CoasterDesigns";
import EmailTemplates from "../pages/admin/EmailTemplates";
import EmailPreview from "../pages/admin/EmailPreview";
import SubscriptionTiers from "../pages/admin/SubscriptionTiers";
import AdminRewards from "../pages/admin/AdminRewards";
// A portal page loaded on demand, with its own Suspense boundary and PageLoadError fallback
// (routes/lazyPortalPage.jsx, shared with routes/business.jsx and routes/venue.jsx).
import lazyPortalPage from "./lazyPortalPage";

// Phase 9 slot-model admin pages (SLOT_MODEL). AdminVenueDetail, AdminWaitlist and
// AdminCategories (through venuesForms) and AdminBusinessDetail (through businessesProfileForm)
// pull in zod and components/portal/form, so they must stay lazy; the others are lazy for
// chunk size. 9a venues & inventory:
const AdminVenues = lazyPortalPage("AdminVenues", () => import("../pages/admin/slot/AdminVenues"));
const AdminVenueDetail = lazyPortalPage("AdminVenueDetail", () => import("../pages/admin/slot/AdminVenueDetail"));
const AdminInventory = lazyPortalPage("AdminInventory", () => import("../pages/admin/slot/AdminInventory"));
const AdminCategoryMap = lazyPortalPage("AdminCategoryMap", () => import("../pages/admin/slot/AdminCategoryMap"));
const AdminWaitlist = lazyPortalPage("AdminWaitlist", () => import("../pages/admin/slot/AdminWaitlist"));
const AdminCategories = lazyPortalPage("AdminCategories", () => import("../pages/admin/slot/AdminCategories"));
// 9b businesses, customers, venue applications, email templates:
const AdminBusinesses = lazyPortalPage("AdminBusinesses", () => import("../pages/admin/slot/AdminBusinesses"));
const AdminBusinessDetail = lazyPortalPage("AdminBusinessDetail", () => import("../pages/admin/slot/AdminBusinessDetail"));
const AdminCustomersPage = lazyPortalPage("AdminCustomersPage", () => import("../pages/admin/slot/AdminCustomers"));
const AdminCustomerDetail = lazyPortalPage("AdminCustomerDetail", () => import("../pages/admin/slot/AdminCustomerDetail"));
const AdminApplications = lazyPortalPage("AdminApplications", () => import("../pages/admin/slot/AdminApplications"));
const AdminEmailTemplates = lazyPortalPage("AdminEmailTemplates", () => import("../pages/admin/slot/AdminEmailTemplates"));
// 9c renewals, billing, products & prices, print:
const AdminRenewals = lazyPortalPage("AdminRenewals", () => import("../pages/admin/slot/AdminRenewals"));
const AdminBilling = lazyPortalPage("AdminBilling", () => import("../pages/admin/slot/AdminBilling"));
const AdminProducts = lazyPortalPage("AdminProducts", () => import("../pages/admin/slot/AdminProducts"));
const AdminPrintCycles = lazyPortalPage("AdminPrintCycles", () => import("../pages/admin/slot/AdminPrintCycles"));
const AdminPrintCycle = lazyPortalPage("AdminPrintCycle", () => import("../pages/admin/slot/AdminPrintCycle"));
// Phase 11 overview (swapped in at /admin/dashboard) and the owner's KPI dashboard:
const AdminOverview = lazyPortalPage("AdminOverview", () => import("../pages/admin/slot/AdminOverview"));
const OwnerKpi = lazyPortalPage("OwnerKpi", () => import("../pages/admin/slot/OwnerKpi"));
// Site redesign Phase 8, the admin leftovers on the same URLs: the three archived lists, the
// full-page email preview and the admin's own profile (AdminProfilePage pulls in zod and
// components/portal/form, so it must stay lazy):
const AdminArchivedCustomers = lazyPortalPage("AdminArchivedCustomers", () => import("../pages/admin/slot/AdminArchivedCustomers"));
const AdminArchivedBusinesses = lazyPortalPage("AdminArchivedBusinesses", () => import("../pages/admin/slot/AdminArchivedBusinesses"));
const AdminArchivedVenues = lazyPortalPage("AdminArchivedVenues", () => import("../pages/admin/slot/AdminArchivedVenues"));
const AdminEmailPreview = lazyPortalPage("AdminEmailPreview", () => import("../pages/admin/slot/AdminEmailPreview"));
const AdminProfilePage = lazyPortalPage("AdminProfilePage", () => import("../pages/admin/slot/AdminProfile"));
// Public-site redesign: the stored site enquiries (list, detail, status, CSV).
const AdminEnquiries = lazyPortalPage("AdminEnquiries", () => import("../pages/admin/slot/AdminEnquiries"));
// Coaster Studio (COASTER_STUDIO): the designs list, the new-design flow and the working screen.
const StudioProjects = lazyPortalPage("StudioProjects", () => import("../pages/admin/slot/studio/StudioProjects"));
const StudioNew = lazyPortalPage("StudioNew", () => import("../pages/admin/slot/studio/StudioNew"));
const StudioSettings = lazyPortalPage("StudioSettings", () => import("../pages/admin/slot/studio/StudioSettings"));
const StudioWorkspace = lazyPortalPage("StudioWorkspace", () => import("../pages/admin/slot/studio/StudioWorkspace"));

export function adminRoutes() {
  const slot = flag("SLOT_MODEL");
  const studio = slot && flag("COASTER_STUDIO");
  return (
    <>
      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route element={<RequireAdmin />}>
        <Route path="/admin/dashboard" element={slot ? <AdminOverview /> : <AdminDashboard />} />
        {slot && <Route path="/admin/owner" element={<OwnerKpi />} />}
        <Route path="/admin/customers" element={slot ? <AdminCustomersPage /> : <AdminCustomers />} />
        <Route path="/admin/customers/archived" element={slot ? <AdminArchivedCustomers /> : <ArchivedCustomers />} />
        <Route path="/admin/customers/:customerId" element={slot ? <AdminCustomerDetail /> : <CustomerDetails />} />
        <Route path="/admin/businesses" element={slot ? <AdminBusinesses /> : <BusinessManagement />} />
        <Route path="/admin/businesses/:businessId/edit" element={slot ? <AdminBusinessDetail /> : <AdminBusinessEdit />} />
        {slot && <Route path="/admin/businesses/:businessId/legacy" element={<AdminBusinessEdit />} />}
        <Route path="/admin/businesses/archived" element={slot ? <AdminArchivedBusinesses /> : <ArchivedBusinesses />} />
        <Route path="/admin/locations" element={slot ? <AdminVenues /> : <LocationManagement />} />
        <Route path="/admin/locations/:locationId" element={slot ? <AdminVenueDetail /> : <LocationDetail />} />
        {slot && <Route path="/admin/locations/:locationId/legacy" element={<LocationDetail />} />}
        <Route path="/admin/locations/archived" element={slot ? <AdminArchivedVenues /> : <ArchivedLocations />} />
        <Route path="/admin/venue-applications" element={slot ? <AdminApplications /> : <VenueApplications />} />
        {slot && <Route path="/admin/enquiries" element={<AdminEnquiries />} />}
        <Route
          path="/admin/coaster-designs"
          element={slot ? <Navigate to="/admin/print" replace /> : <CoasterDesigns />}
        />
        <Route path="/admin/email-templates" element={slot ? <AdminEmailTemplates /> : <EmailTemplates />} />
        <Route path="/admin/email-templates/:templateId/preview" element={slot ? <AdminEmailPreview /> : <EmailPreview />} />
        <Route path="/admin/subscriptions" element={slot ? <AdminProducts /> : <SubscriptionTiers />} />
        {slot && <Route path="/admin/subscription-tiers" element={<Navigate to="/admin/subscriptions" replace />} />}
        {slot && <Route path="/admin/inventory" element={<AdminInventory />} />}
        {slot && <Route path="/admin/category-map" element={<AdminCategoryMap />} />}
        {slot && <Route path="/admin/waitlist" element={<AdminWaitlist />} />}
        {slot && <Route path="/admin/categories" element={<AdminCategories />} />}
        {slot && <Route path="/admin/renewals" element={<AdminRenewals />} />}
        {slot && <Route path="/admin/billing" element={<AdminBilling />} />}
        {slot && <Route path="/admin/print" element={<AdminPrintCycles />} />}
        {slot && <Route path="/admin/print/:cycleId" element={<AdminPrintCycle />} />}
        {studio && <Route path="/admin/coaster-studio" element={<StudioProjects />} />}
        {studio && <Route path="/admin/coaster-studio/new" element={<StudioNew />} />}
        {studio && <Route path="/admin/coaster-studio/settings" element={<StudioSettings />} />}
        {studio && <Route path="/admin/coaster-studio/:projectId" element={<StudioWorkspace />} />}
        {flag('REWARDS') && <Route path="/admin/rewards" element={<AdminRewards />} />}
        <Route path="/admin/profile" element={slot ? <AdminProfilePage /> : <AdminProfile />} />
      </Route>
      {/* Unknown (or flagged-off) admin paths: NotFound without site chrome. */}
      <Route path="/admin/*" element={<NotFound />} />
    </>
  );
}
